import React, { useEffect, useState } from "react";
import '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faBriefcase, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { VerticalTimeline, VerticalTimelineElement }  from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import '../assets/styles/Profile.scss'
import cambridge from '../assets/images/university_of_cambridge.png';
import eth from '../assets/images/eth_pos.svg';
import pmp from '../assets/images/pmp.jpg';
import hector from '../assets/images/hector_management.jpg';
import qzabre from '../assets/images/qzabre_logo.png';
import aquila from '../assets/images/affiliations/aquila_aff.svg';
import Chip from '@mui/material/Chip';

// Shared styling for timeline cards (theme-aware via CSS variables)
const cardStyle = {
  background: 'var(--surface)',
  color: 'var(--text)',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  borderRadius: '18px',
};
const arrowStyle = { borderRight: '7px solid var(--surface)' };
const iconWork = { background: 'var(--accent)', color: '#fff' };
const iconEdu = { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' };

function Profile() {
  const [activeHoverPage, setActiveHoverPage] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("dark-mode")
  );
  const [showCoursework, setShowCoursework] = useState(false);

  useEffect(() => {
    document.body.style.overflow = activeHoverPage ? "hidden" : "auto";
  }, [activeHoverPage]);

  useEffect(() => {
      const observer = new MutationObserver(() => {
        setIsDarkMode(document.body.classList.contains("dark-mode"));
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => observer.disconnect();
    }, []);


    // Die Gruppen folgen dem Lebenslauf, damit Website und CV dieselbe
    // Gliederung zeigen: Silizium, Programmierung, Mathematik, ML, Finanzen,
    // Daten und Infrastruktur.
    const labels_silicon = [
    "Chip design",
    "Device & circuit simulation",
    "Cleanroom fabrication",
    "Crossbar arrays",
    "In-memory & event-driven compute",
    "On-chip interconnect",
    "Analogue circuit design",
    "Memory hierarchy & dataflow",
    ];
    const labels_programming = [
    "Python (NumPy, Pandas, statsmodels)",
    "C++",
    "Matlab",
    "SQL",
    "Git",
    ];
    const labels_quant = [
    "Time series (GARCH, cointegration)",
    "Regression & regularisation",
    "PCA / factor models",
    "Optimization",
    "Numerical methods",
    "PDEs",
    ];
    const labels_ml = [
    "PyTorch",
    "scikit-learn",
    "LightGBM / XGBoost",
    "SHAP",
    "Time-series cross-validation",
    "Bagging",
    "Supervised & unsupervised learning",
    ];
    const labels_finance = [
    "Bloomberg terminal (BQL, PORT)",
    "Asset classes",
    "Backtesting",
    "Fixed income & curve modelling",
    "Credit analysis",
    "Portfolio construction & risk attribution",
    ];
    const labels_infra = [
    "MCP",
    "FastAPI",
    "REST APIs",
    "SQL databases",
    "Research-to-production pipelines",
    ];
    const labels_soft = [
    "Analytical problem solving",
    "Teamwork",
    "Leadership",
    "Resilience",
    "Clear, direct communication",
    ];




  return (
    <div id="profile">
      <div className="items-container">
        <h1>Profile</h1>
        <p>
          I am a Research Analyst developing systematic fixed income strategies at <strong>Aquila Asset Management AG</strong>, a boutique asset manager in Baar, Switzerland, where I work across the full quantitative research cycle — from signal research to production.
        </p>
        <p>
          In parallel, I am a <strong>Portfolio Manager and Quantitative Analyst</strong> in the Portfolio Management Program at the University of Zurich (PMP Zurich) — a program run in cooperation with UBS, QRT, and UZH, supervised by Dr. Alexandre Ziegler and Prof. Thorsten Hens. There, as part of a small team in which each member acts as a portfolio manager, I help manage a 2M+ CHF portfolio, designing predictive signals such as momentum, low-beta, and overreaction effects and deploying systematic strategies across equities, bonds, FX, and derivatives, combining statistical models (GARCH, regression) with machine learning (random forests) to generate risk-adjusted alpha (Sharpe &gt; 1).
        </p>
        <p>
          My quantitative work is grounded in a physics &amp; engineering research background. I pursued a PhD in Neuromorphic Computing at ETH Zurich, developing ML-driven architectures for low-latency, low-energy AI inference, before moving full-time into quantitative finance. I hold an MPhil in Physics from the University of Cambridge and a BSc in Electrical Engineering from ETH Zurich (GPA 5.62/6.0, top 6%), and I am a member of the Hector Fellow Academy, enrolled in its MBA Fundamentals Program.
        </p>
        <p>
          Alongside my formal training, I built a deep focus in quantitative finance and advanced mathematics through targeted coursework and independent work — mathematical finance, risk management, stochastic calculus, optimization, time-series analysis, backtesting, machine learning, and advanced statistics — which I have applied in projects spanning regime-shift detection, statistical arbitrage with automated execution, and AI-based fraud detection for private banking.
        </p>
        <p></p>
        <button className="special-btn" onClick={() => setShowCoursework(!showCoursework)}>
          {showCoursework ? "↑ Hide coursework & focus" : "↓ Show extracurricular coursework & focus"}
        </button>
        {showCoursework && (
          <div className="coursework-extension">
            <h2>Advanced Coursework &amp; Independent Focus</h2>

            <div className="coursework-columns">
              <div>
                <h3>Mathematics</h3>
                <ul>
                  <li>Linear Algebra</li>
                  <li>Probability Theory</li>
                  <li>Statistics</li>
                  <li>Stochastic Calculus</li>
                  <li>Mathematical Finance</li>
                </ul>
              </div>

              <div>
                <h3>Quantitative Methods</h3>
                <ul>
                  <li>Time Series Analysis</li>
                  <li>Statistical Learning</li>
                  <li>Convex Optimization</li>
                  <li>Numerical Methods</li>
                </ul>
              </div>

              <div>
                <h3>Finance</h3>
                <ul>
                  <li>Corporate Finance</li>
                  <li>Advanced Asset Management</li>
                  <li>Financial Risk Management</li>
                  <li>Fintech</li>
                </ul>
              </div>

              <div>
                <h3>Machine Learning &amp; AI</h3>
                <ul>
                  <li>Introduction to Machine Learning</li>
                  <li>Neuromorphic Intelligence &amp; Engineering</li>
                  <li>Learning Algorithms of Machines</li>
                  <li>In-Memory Computing Algorithms</li>
                  <li>Agentic AI Deployment</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        <h3 className="skills-heading">
          Key skills
        </h3>
        <div className="skill-groups">
          <div className="skill-group">
            <span className="chip-title">Silicon &amp; Architecture</span>
            <div className="flex-chips">
              {labels_silicon.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
          <div className="skill-group">
            <span className="chip-title">Programming</span>
            <div className="flex-chips">
              {labels_programming.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
          <div className="skill-group">
            <span className="chip-title">Math</span>
            <div className="flex-chips">
              {labels_quant.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
          <div className="skill-group">
            <span className="chip-title">Machine Learning</span>
            <div className="flex-chips">
              {labels_ml.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
          <div className="skill-group">
            <span className="chip-title">Finance</span>
            <div className="flex-chips">
              {labels_finance.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
          <div className="skill-group">
            <span className="chip-title">Data &amp; Infrastructure</span>
            <div className="flex-chips">
              {labels_infra.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
          <div className="skill-group">
            <span className="chip-title">Soft skills</span>
            <div className="flex-chips">
              {labels_soft.map((label, index) => (
                <Chip key={index} className='chip' label={label} />
              ))}
            </div>
          </div>
        </div>

        <p className="interests-line">
          <span>Beyond the desk</span> Golf · Rowing · Tennis · Kitesurfing
        </p>

        <h2></h2>

        <VerticalTimeline>
          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2026 - present"
            iconStyle={iconWork}
            icon={<FontAwesomeIcon icon={faChartLine} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>Research Analyst — Systematic Fixed Income
              <a
                href="https://www.aam.ch/en/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={aquila}
                  alt="Aquila Asset Management"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "2.4rem", width: "8rem", marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">Aquila Asset Management AG</h4>
            <p>
              Developing systematic fixed income strategies at a boutique Swiss asset manager, across the full quantitative research cycle.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("aquila")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2025 - present"
            iconStyle={iconWork}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>Portfolio Manager &amp; Quantitative Analyst
              <a
                href="https://www.cpm.uzh.ch/en.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={pmp}
                  alt="PMP Zurich Logo"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "4rem", marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">PMP Zurich · in cooperation with UBS &amp; QRT</h4>
            <p>
              Managing a 2M+ CHF portfolio as part of a small team in which each member acts as a portfolio manager. Designing predictive signals and backtesting systematic strategies across equities, bonds, FX, and derivatives to maximize Sharpe ratio and alpha.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("pmp")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2024 - 2026"
            iconStyle={iconWork}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>PhD Researcher — Neuromorphic Computing
              <a
                href="https://ee.ethz.ch/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={eth}
                  alt="ETH Zurich Logo"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "4rem", width: "10rem", marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">ETH Zurich</h4>
            <p>
              ML-driven computing architectures for low-latency, low-energy AI inference. Left to move full-time into quantitative finance.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("phd")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2024 - present"
            iconStyle={iconEdu}
            icon={<FontAwesomeIcon icon={faGraduationCap} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>MBA Fundamentals Programme
              <a
                href="https://www.hectorschool.kit.edu/mba-fundamentals-program.php"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={hector}
                  alt="Hector Logo"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "2rem", width:"10rem",marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">Hector School of Eng. &amp; Management</h4>
            <p>
              Management training in corporate finance, accounting, operations, business administration, and leadership.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("hector")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2023 - 2025"
            iconStyle={iconEdu}
            icon={<FontAwesomeIcon icon={faGraduationCap} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>MPhil in Physics
              <a
                href="https://www.cam.ac.uk/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={cambridge}
                  alt="Cam Logo"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "2.5rem", width:"10rem",marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">University of Cambridge</h4>
            <p>
              Nanophotonics with a focus on statistical modeling. Commended by examiners as of doctoral standard.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("cambridge")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2022 - 2023"
            iconStyle={iconWork}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>R&amp;D Intern
              <a
                href="https://www.qzabre.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={qzabre}
                  alt="qzabre Logo"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "2.5rem", width:"10rem",marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">QZabre Ltd</h4>
            <p>
              Software (Python, C++) and hardware development for quantum sensing — improved product speed by 200% and applied statistical analysis to extract actionable insights.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("qzabre")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2019 - 2022"
            iconStyle={iconEdu}
            icon={<FontAwesomeIcon icon={faGraduationCap} />}
          >
            <h3 className="vertical-timeline-element-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>BSc in Electrical Engineering
              <a
                href="https://ee.ethz.ch/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={eth}
                  alt="eth Logo"
                  className="profile-hover-page-image clickable-image"
                  style={{ height: "4rem", width: "10rem", marginLeft: "0.5rem" }}
                />
              </a>
            </h3>
            <h4 className="vertical-timeline-element-subtitle">ETH Zurich · GPA 5.62/6.0 (top 6%)</h4>
            <p>
              Focus on financial risk, machine learning, communication networks, linear algebra, probability &amp; statistics.
            </p>
            <button className="details-btn" onClick={() => setActiveHoverPage("bachelor")}>
              Details
            </button>
          </VerticalTimelineElement>

          <VerticalTimelineElement
            className="vertical-timeline-element--work"
            contentStyle={cardStyle}
            contentArrowStyle={arrowStyle}
            date="2018 - 2021"
            iconStyle={iconWork}
            icon={<FontAwesomeIcon icon={faBriefcase} />}
          >
            <h3 className="vertical-timeline-element-title">Founder — Self-employed</h3>
            <h4 className="vertical-timeline-element-subtitle">Independent venture</h4>
            <p>
              Managed financial planning, budgeting, pricing, and operational decision-making end to end.
            </p>
          </VerticalTimelineElement>
        </VerticalTimeline>



        {/* ================= OVERLAY ================= */}
        {activeHoverPage && (
        <div className="hover-page-overlay" onClick={() => setActiveHoverPage(null)}>
          <div className="hover-page-content" onClick={(e) => e.stopPropagation()}>

            {activeHoverPage === "aquila" && (
              <>
                <div className="hover-page-header">
                  <h1>Research Analyst — Systematic Fixed Income</h1>
                  <img src={aquila} className="profile-hover-page-image" style={{width:"8rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  At Aquila Asset Management AG, a boutique Swiss asset manager, I develop an end-to-end research platform for systematic emerging-market sovereign selection. The data layer automates the ingestion, cleaning and validation of market data across multiple providers. On this basis, a machine-learning boosted ranking model is residualised against an emerging-market beta factor in order to isolate idiosyncratic value, complemented by statistical timing signals that steer aggregate risk exposure to improve risk-adjusted returns and drawdown behaviour.
                </p>
                <p>
                  I own the full cycle from research prototype to production code, including the analysis framework and its deployment. Specifics of the strategies and infrastructure are confidential — NDA applies.
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Research platform architecture, from data layer to production deployment</li>
                  <li>Automated multi-provider data ingestion, cleaning and validation</li>
                  <li>Boosted ranking models and factor residualisation</li>
                  <li>Statistical timing signals for risk-exposure steering</li>
                  <li>Fixed income and emerging-market sovereign analysis</li>
                </ul>
                <a
                  href="https://www.aam.ch/en/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → Aquila Asset Management
                </a>
                <p></p>
              </>
            )}

            {activeHoverPage === "pmp" && (
              <>
                <div className="hover-page-header">
                  <h1>Portfolio Manager &amp; Quantitative Analyst</h1>
                  <img src={pmp} className="profile-hover-page-image" style={{width:"8rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  In the Portfolio Management Program (PMP Zurich) at the University of Zurich, run in cooperation with UBS, QRT, and UZH, I manage a 2M+ CHF real-money portfolio as part of a small team in which each member acts as a portfolio manager, under cost, liquidity, and risk constraints. I analyze historical Bloomberg data to identify statistical signals — momentum, low-beta, and overreaction effects — and develop and implement systematic trading strategies across equities, bonds, FX, and futures. Performance, measured by Sharpe ratio and alpha, is optimized through out-of-sample backtesting and ongoing signal monitoring, combining statistical models (GARCH, regression) with machine learning (random forests) to systematically generate alpha (Sharpe &gt; 1).
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Quantitative and statistical analysis, and systematic strategy implementation</li>
                  <li>Real-money portfolio management under real-world constraints</li>
                  <li>Backtesting, signal monitoring, and performance optimization</li>
                  <li>Collaborative decision-making in live investment environments</li>
                </ul>
                <a
                  href="https://www.cpm.uzh.ch/en.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → PMP Zurich
                </a>
                <p></p>
              </>
            )}

            {activeHoverPage === "phd" && (
              <>
                <div className="hover-page-header">
                  <h1>PhD Researcher — Neuromorphic Computing</h1>
                  <img src={eth} className="profile-hover-page-image" style={{width:"15rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  As a PhD researcher at ETH Zurich, my work focused on developing computer architectures using memristive technologies to accelerate AI — increasing computational speed and reducing energy consumption at scale. The research spanned hardware, software, and algorithm development, enabling on-chip learning and brain-inspired computing through neuromorphic architectures. This rigorous research methodology — designing experiments, working with large datasets, and translating theory into implementation — directly underpins my quantitative work today. I left the program to move full-time into quantitative finance.
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Rigorous research methodology on large, noisy datasets</li>
                  <li>Algorithm development for in-memory and on-chip learning</li>
                  <li>Hardware-software co-design and performance optimization</li>
                  <li>Translating theory into production-grade implementation</li>
                </ul>
                <a
                  href="https://ee.ethz.ch/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → Electrical Engineering at ETH Zurich
                </a>
                <p></p>
              </>
            )}

            {activeHoverPage === "hector" && (
              <>
                <div className="hover-page-header">
                  <h1>MBA Fundamentals Programme</h1>
                  <img src={hector} className="profile-hover-page-image" style={{width:"15rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  The HECTOR School of Engineering &amp; Management offers the MBA Fundamentals Program, designed for PhD students to complement technical expertise with core business and management skills. The program covers strategy, finance, operations, innovation management, leadership, sustainability, data analytics, and people management through official university MBA modules — building a solid foundation for bridging technology and business.
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Business strategy and innovation management</li>
                  <li>Finance, accounting, and economic decision-making</li>
                  <li>Leadership and project management</li>
                  <li>Technology commercialization and entrepreneurial thinking</li>
                </ul>
                <a
                  href="https://www.hectorschool.kit.edu/index.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → Hector School of Technology and Business
                </a>
                <p></p>
              </>
            )}

            {activeHoverPage === "cambridge" && (
              <>
                <div className="hover-page-header">
                  <h1>MPhil in Physics</h1>
                  <img src={cambridge} className="profile-hover-page-image" style={{width:"15rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  MPhil in Physics at the University of Cambridge, with a focus on nanophotonics, experimental design, and theoretical modeling of nanoscale dynamics via electromagnetic waves — work commended by examiners as of doctoral standard. I built and conducted experiments, applied advanced mathematics to model nanoscale systems, and analyzed data using statistical and computational techniques, with strong emphasis on the intersection of theory and experiment.
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Statistical data analysis and computational methods</li>
                  <li>Theoretical modeling using advanced mathematics</li>
                  <li>Experimental design and laboratory techniques</li>
                  <li>Advanced physics problem-solving and optics</li>
                </ul>
                <a
                  href="https://www.cam.ac.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → University of Cambridge
                </a>
                <p></p>
              </>
            )}

            {activeHoverPage === "qzabre" && (
              <>
                <div className="hover-page-header">
                  <h1>R&amp;D Intern</h1>
                  <img src={qzabre} className="profile-hover-page-image" style={{width:"10rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  Internship at QZabre, Zurich, developing software (Python, C++) and hardware for quantum sensing microscopes. I designed and implemented system components, built software for the end product — improving product speed by 200% — performed statistical data analysis, and presented results to the team to guide product optimization.
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Software and hardware development</li>
                  <li>Statistical data analysis and visualization</li>
                  <li>Experimental system design and implementation</li>
                  <li>Communicating and presenting technical findings</li>
                </ul>
                <a
                  href="https://www.qzabre.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → QZabre Quantum-sensing
                </a>
                <p></p>
              </>
            )}

            {activeHoverPage === "bachelor" && (
              <>
                <div className="hover-page-header">
                  <h1>BSc in Electrical Engineering</h1>
                  <img src={eth} className="profile-hover-page-image" style={{width:"15rem", marginLeft:"3rem"}}/>
                </div>
                <p>
                  Bachelor's studies at ETH Zurich (GPA 5.62/6.0, top 6%) covering linear algebra, probability theory and statistics, and partial differential equations, with added focus on financial risk management, fintech, stochastic calculus, and optimization. Practical projects included mathematical simulation of electromagnetic waves and designing control algorithms for drone systems.
                </p>
                <p>Key skills gained:</p>
                <ul>
                  <li>Advanced mathematics: linear algebra, PDEs, stochastic calculus, optimization</li>
                  <li>Statistical analysis and probability modeling</li>
                  <li>Simulation of physical systems and control algorithms</li>
                  <li>Financial risk management and fintech applications</li>
                </ul>
                <a
                  href="https://ee.ethz.ch/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover-page-link-btn"
                >
                  → Electrical Engineering at ETH Zurich
                </a>
                <p></p>
              </>
            )}

            <button
              className="close-btn"
              onClick={() => setActiveHoverPage(null)}
            >
              ✕
            </button>

          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default Profile;
